// admin-panel/src/pages/DashboardPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Users, Phone, UserCheck, Target,
  Calendar as CalendarIcon, ChevronDown, TrendingUp, TrendingDown
} from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

// --- Re-usable Helper ---
const getToken = () => localStorage.getItem('admin_token');

// Helper to format YYYY-MM-DD for API
const toApiDate = (date) => format(date, 'yyyy-MM-dd');

// =================================================================================
// 1. PAGE-SPECIFIC COMPONENTS
// =================================================================================

/**
 * Modern Stat Card Component
 */
const StatCard = ({ title, value, icon, theme, loading, subtext, trend }) => {
  const isPositive = trend && trend.startsWith('+');
  const isNegative = trend && trend.startsWith('-');
  const isTarget = trend && trend.includes('Target');

  let TrendIcon = TrendingUp;
  let trendColor = '#6B7280';

  if (isPositive) {
    TrendIcon = TrendingUp;
    trendColor = '#059669';
  } else if (isNegative) {
    TrendIcon = TrendingDown;
    trendColor = '#ef4444';
  } else if (isTarget) {
    TrendIcon = Target;
    trendColor = (trend === 'Above Target') ? '#059669' : '#6B7280';
  }

  return (
    <div className={`stat-card ${theme}`}>
      <div className="stat-card-inner">
        <div className="stat-icon-wrapper">
          {icon}
        </div>
        <div className="stat-content">
          <span className="stat-title">{title}</span>
          <span className="stat-value">
            {loading ? <Spinner /> : value}
          </span>
          {subtext && <span className="stat-subtext">{subtext}</span>}
          {trend && !loading && (
            <div className="stat-trend" style={{ color: trendColor }}>
              <TrendIcon size={14} />
              <span>{trend}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Loading Spinner
 */
const Spinner = () => (
  <svg className="spinner" viewBox="0 0 50 50">
    <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
  </svg>
);

/**
 * Avatar Component
 */
const Avatar = ({ name }) => {
  const initial = name ? name.trim()[0].toUpperCase() : '?';
  const hashCode = (str) => {
    if (!str) str = '?';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
  };
  const colors = [
    { bg: '#667eea', text: '#ffffff' },
    { bg: '#f5576c', text: '#ffffff' },
    { bg: '#4facfe', text: '#ffffff' },
    { bg: '#43e97b', text: '#ffffff' },
  ];
  const color = colors[Math.abs(hashCode(name)) % colors.length];
  return (
    <div
      className="avatar-circle"
      style={{ backgroundColor: color.bg, color: color.text }}
      title={name}
    >
      {initial}
    </div>
  );
};

/**
 * Dashboard Header with Date Filters - Redesigned
 */
const DashboardHeader = ({ onDateChange, isLoading }) => {
  const [dateRange, setDateRange] = useState({ from: subDays(new Date(), 29), to: new Date() });
  const [preset, setPreset] = useState('Last 30 Days');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [adminRole, setAdminRole] = useState('Admin');

  useEffect(() => {
    onDateChange(dateRange);
  }, [dateRange, onDateChange]);

  useEffect(() => {
    const role = localStorage.getItem('admin_role') || 'Admin';
    setAdminRole(role);
  }, []);

  const setPresetRange = (name) => {
    const today = new Date();
    let from, to = today;

    if (name === 'This Week') {
      from = startOfWeek(today, { weekStartsOn: 1 });
      to = endOfWeek(today, { weekStartsOn: 1 });
    } else if (name === 'This Month') {
      from = startOfMonth(today);
      to = endOfMonth(today);
    } else if (name === 'Last 7 Days') {
      from = subDays(today, 6);
    } else {
      from = subDays(today, 29);
    }

    setPreset(name);
    setDateRange({ from, to });
    setIsPickerOpen(false);
  };

  const handleDaySelect = (range) => {
    if (range?.from && range?.to) {
      setDateRange(range);
      setPreset('Custom');
      setIsPickerOpen(false);
    } else if (range?.from) {
      setDateRange({ from: range.from, to: range.from });
      setPreset('Custom');
    }
  };

  const displayDate = dateRange.from && dateRange.to
    ? `${format(dateRange.from, 'd LLL yyyy')} - ${format(dateRange.to, 'd LLL yyyy')}`
    : 'Select Date Range';

  return (
    <div className="dashboard-header">
      {/* --- NEW: Background Layer handles clipping independently --- */}
      <div className="header-bg-decoration"></div>
      {/* ----------------------------------------------------------- */}

      <div className="header-content">
        <div className="header-badge">Analytics Dashboard</div>
        <h1 className="page-title">
          Welcome back, <span className="gradient-text">{adminRole}</span>
        </h1>
        <p className="page-subtitle">Here's your comprehensive referral overview and performance metrics</p>
      </div>

      <div className="date-filter-container">
        <div className="filter-label">Time Period:</div>
        <div className="filter-buttons">
          {['Last 7 Days', 'Last 30 Days', 'This Week', 'This Month'].map(p => (
            <button
              key={p}
              disabled={isLoading}
              className={`preset-btn ${preset === p ? 'active' : ''}`}
              onClick={() => setPresetRange(p)}
            >
              {p}
            </button>
          ))}

          <div className="date-picker-wrapper">
            <button
              disabled={isLoading}
              className={`date-picker-toggle ${preset === 'Custom' ? 'active' : ''}`}
              onClick={() => setIsPickerOpen(!isPickerOpen)}
            >
              <CalendarIcon size={16} />
              <span>{preset === 'Custom' ? displayDate : 'Custom Range'}</span>
              <ChevronDown size={16} />
            </button>

            {isPickerOpen && (
              <div className="date-picker-popover" onMouseLeave={() => setIsPickerOpen(false)}>
                <DayPicker
                  mode="range"
                  selected={dateRange}
                  onSelect={handleDaySelect}
                  numberOfMonths={2}
                  defaultMonth={dateRange.from}
                  footer={<button className="date-picker-close" onClick={() => setIsPickerOpen(false)}>Apply Selection</button>}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Admissions Over Time Chart
 */
const AdmissionsChart = ({ data, loading }) => (
  <div className="chart-card large-chart">
    <div className="chart-header">
      <h3 className="card-title">Admissions Trend Analysis</h3>
      <span className="chart-badge">Real-time Data</span>
    </div>
    {loading ? (
      <div className="chart-loader"><Spinner /></div>
    ) : (
      <ResponsiveContainer width="100%" height={380}>
        <AreaChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorAdmissions" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#667eea" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey="label"
            fontSize={13}
            stroke="#6B7280"
            tickLine={false}
            axisLine={false}
            dy={10}
          />
          <YAxis
            fontSize={13}
            stroke="#6B7280"
            tickLine={false}
            axisLine={false}
            dx={-10}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#FFFFFF'
            }}
            labelStyle={{ color: '#1F2937', fontWeight: '700', marginBottom: '4px' }}
            itemStyle={{ color: '#667eea', fontWeight: '600' }}
          />
          <Area
            type="monotone"
            dataKey="value"
            name="Admissions"
            stroke="#667eea"
            strokeWidth={3}
            fillOpacity={1}
            fill="url(#colorAdmissions)"
          />
        </AreaChart>
      </ResponsiveContainer>
    )}
  </div>
);

/**
 * Referral Funnel Chart
 */
const FunnelChart = ({ data, loading }) => {
  const COLORS = ['#667eea', '#f5576c', '#43e97b'];
  return (
    <div className="chart-card small-chart">
      <div className="chart-header">
        <h3 className="card-title">Conversion Funnel</h3>
        <span className="chart-badge">Overview</span>
      </div>
      {loading ? (
        <div className="chart-loader"><Spinner /></div>
      ) : (
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="45%"
              innerRadius={85}
              outerRadius={130}
              fill="#8884d8"
              paddingAngle={3}
              label={({ name, percent, value }) => `${value} (${(percent * 100).toFixed(0)}%)`}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [value, name]}
              contentStyle={{
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                backgroundColor: '#FFFFFF'
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="circle"
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

/**
 * Recent Activity Component
 */
const RecentActivity = ({ loading }) => {
  const [referrals, setReferrals] = useState([]);

  const fetchRecent = useCallback(async () => {
    try {
      // This endpoint returns an object { referrals: [], totalItems: ... }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/referrals`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch recent referrals.');
      const data = await response.json();

      // --- FIX: Access data.referrals instead of data directly ---
      const refs = data.referrals || [];
      setReferrals(refs.slice(0, 5));
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return "a moment ago";
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + "y ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + "mo ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + "d ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + "h ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + "m ago";
    return "just now";
  }

  return (
    <div className="chart-card recent-activity">
      <div className="chart-header">
        <h3 className="card-title">Recent Registrations</h3>
        <a href="/referrals" className="view-all-link">View all →</a>
      </div>
      <div className="recent-body">
        {loading ? (
          <div className="chart-loader"><Spinner /></div>
        ) : (
          <ul className="recent-list">
            {referrals.length > 0 ? referrals.map(ref => (
              <li key={ref.id} className="recent-item">
                <Avatar name={ref.full_name} />
                <div className="item-info">
                  <span className="item-name">{ref.full_name}</span>
                  <span className="item-detail">Referred by: {ref.referrer_name}</span>
                </div>
                <span className="item-time">{getTimeAgo(ref.created_at)}</span>
              </li>
            )) : (
              <div className="no-data-message">
                <p className="no-data-text">No recent signups yet.</p>
                <p className="no-data-subtext">New registrations will appear here</p>
              </div>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};


// =================================================================================
// 2. MAIN DASHBOARD PAGE COMPONENT
// =================================================================================

const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalReferrals: { value: 0, trendText: '...' },
    totalCallsMade: { value: 0, trendText: '...' },
    totalAdmissions: { value: 0, trendText: '...' },
    conversionRate: { value: 0, trendText: '...' }
  });

  const [chartData, setChartData] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 29),
    to: new Date()
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (currentRange, isSilent = false) => {
    const token = getToken();
    if (!token) {
      setError('Authentication token not found.');
      setLoading(false);
      return;
    }

    if (!isSilent) setLoading(true); // Only show loader if not silent
    setError('');

    const startDate = toApiDate(currentRange.from);
    const endDate = toApiDate(currentRange.to);

    try {
      const [statsRes, chartRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/stats?startDate=${startDate}&endDate=${endDate}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/chart?startDate=${startDate}&endDate=${endDate}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!statsRes.ok) throw new Error('Failed to fetch stats.');
      if (!chartRes.ok) throw new Error('Failed to fetch chart data.');

      const statsData = await statsRes.json();
      const chartData = await chartRes.json();

      setStats(statsData);
      setChartData(chartData);

    } catch (err) {
      if (!isSilent) setError(err.message); // Only show error if not silent
      console.error(err);
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, []);

  // Initial Fetch
  useEffect(() => {
    fetchData(dateRange);
  }, [dateRange, fetchData]);

  // Polling for Real-time Updates (every 5 seconds)
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchData(dateRange, true); // Silent update
    }, 5000);

    return () => clearInterval(intervalId);
  }, [dateRange, fetchData]);

  const { conversionRateValue, funnelData } = useMemo(() => {
    if (!stats.totalReferrals) {
      return { conversionRateValue: '0.0%', funnelData: [] };
    }

    const { totalReferrals, totalCallsMade, totalAdmissions, conversionRate } = stats;

    const funnel = [
      { name: 'Registered', value: totalReferrals.value },
      { name: 'Called', value: totalCallsMade.value },
      { name: 'Admitted', value: totalAdmissions.value },
    ];

    return {
      conversionRateValue: conversionRate.value.toFixed(1) + '%',
      funnelData: funnel
    };
  }, [stats]);

  return (
    <div className="admin-dashboard-container">
      <PageStyles />

      <DashboardHeader onDateChange={setDateRange} isLoading={loading} />

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="stats-grid">
        <StatCard
          title="Total Referrals"
          value={stats.totalReferrals.value}
          loading={loading}
          theme="theme-indigo"
          icon={<Users size={28} />}
          subtext="New users registered"
          trend={stats.totalReferrals.trendText}
        />
        <StatCard
          title="Contacts Made"
          value={stats.totalCallsMade.value}
          loading={loading}
          theme="theme-amber"
          icon={<Phone size={28} />}
          subtext="Leads contacted"
          trend={stats.totalCallsMade.trendText}
        />
        <StatCard
          title="Total Admissions"
          value={stats.totalAdmissions.value}
          loading={loading}
          theme="theme-emerald"
          icon={<UserCheck size={28} />}
          subtext="Converted students"
          trend={stats.totalAdmissions.trendText}
        />
        <StatCard
          title="Conversion Rate"
          value={conversionRateValue}
          loading={loading}
          theme="theme-violet"
          icon={<Target size={28} />}
          subtext="Referrals to Admitted"
          trend={stats.conversionRate.trendText}
        />
      </div>

      <div className="dashboard-main-grid">
        <AdmissionsChart data={chartData} loading={loading} />
        <FunnelChart data={funnelData} loading={loading} />
      </div>

      <div style={{ marginTop: '32px' }}>
        <RecentActivity loading={loading} />
      </div>
    </div>
  );
};

// =================================================================================
// 3. STYLESHEET (CSS-in-JS)
// =================================================================================
const PageStyles = () => (
  <style>{`
    /* --- Animations --- */
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    @keyframes gradientFlow {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    .admin-dashboard-container {
      animation: fadeInUp 0.6s ease-out;
      padding: 0;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }

    .spinner {
      animation: spin 1s linear infinite;
      width: 2.25rem;
      height: 2.25rem;
      color: #667eea;
    }
    .spinner .path {
      stroke: currentColor;
      stroke-linecap: round;
    }
    
    .error-banner {
      background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
      color: #991b1b;
      border: 2px solid #fecaca;
      padding: 1.25rem 1.5rem;
      border-radius: 16px;
      margin-bottom: 2rem;
      font-weight: 600;
      box-shadow: 0 4px 15px rgba(239, 68, 68, 0.1);
      animation: fadeInUp 0.4s ease-out;
    }

    /* --- Header & Filters - REVISED FOR DROPDOWN OVERFLOW --- */
    .dashboard-header {
      position: relative;
      margin-bottom: 40px;
      padding: 48px 40px;
      /* REMOVED overflow: hidden so popover can show */
      overflow: visible; 
      /* REMOVED background here, moved to .header-bg-decoration */
    }
    
    .header-bg-decoration {
      position: absolute;
      top: 0; left: 0; width: 100%; height: 100%;
      border-radius: 24px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      overflow: hidden; /* Clip the circle here */
      z-index: 0;
      box-shadow: 0 20px 60px rgba(102, 126, 234, 0.3);
    }
    .header-bg-decoration::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
      animation: spin 20s linear infinite;
    }
    
    .header-content {
      position: relative;
      z-index: 2; /* Above background */
      margin-bottom: 32px;
    }
    
    .header-badge {
      display: inline-block;
      background: rgba(255, 255, 255, 0.2);
      color: #ffffff;
      padding: 8px 20px;
      border-radius: 50px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 16px;
      backdrop-filter: blur(10px);
    }
    .page-title {
      margin: 0;
      font-size: clamp(2rem, 5vw, 3rem);
      font-weight: 900;
      color: #FFFFFF;
      letter-spacing: -0.025em;
      line-height: 1.2;
      margin-bottom: 12px;
    }
    .gradient-text {
      background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
      background-size: 200% 200%;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: gradientFlow 3s ease infinite;
      display: inline-block;
    }
    .page-subtitle {
      font-size: 1.125rem;
      color: rgba(255, 255, 255, 0.9);
      margin: 0;
      font-weight: 400;
    }
    
    .date-filter-container {
      position: relative;
      z-index: 2; /* Above background */
    }
    .filter-label {
      color: rgba(255, 255, 255, 0.9);
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 12px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .filter-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
    .preset-btn, .date-picker-toggle {
      background: rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(10px);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      padding: 12px 24px;
      font-size: 14px;
      font-weight: 700;
      color: #FFFFFF;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .preset-btn:hover, .date-picker-toggle:hover {
      background: rgba(255, 255, 255, 0.25);
      border-color: rgba(255, 255, 255, 0.4);
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    }
    .preset-btn.active, .date-picker-toggle.active {
      background: #FFFFFF;
      color: #667eea;
      border-color: #FFFFFF;
      box-shadow: 0 8px 25px rgba(255, 255, 255, 0.3);
    }
    .preset-btn:disabled, .date-picker-toggle:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
    .date-picker-wrapper {
      position: relative;
    }
    .date-picker-popover {
      position: absolute;
      top: 110%;
      right: 0;
      z-index: 100; /* Higher than header-bg-decoration */
      background: white;
      border-radius: 16px;
      border: 1px solid #E5E7EB;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
      overflow: hidden;
    }
    
    /* React Day Picker Styles */
    :root {
      --rdp-cell-size: 40px;
      --rdp-accent-color: #667eea;
      --rdp-background-color: #eef2ff;
      --rdp-accent-color-dark: #4338ca;
      --rdp-background-color-dark: #c7d2fe;
    }
    .rdp-button:hover:not([disabled]):not(.rdp-day_selected) {
      background-color: var(--rdp-background-color);
    }
    .date-picker-close {
      width: calc(100% - 32px);
      margin: 16px;
      padding: 12px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 10px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.3s ease;
    }
    .date-picker-close:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
    }

    /* --- Stats Grid --- */
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 24px;
      margin-bottom: 40px;
    }
    .stat-card {
      background: #FFFFFF;
      border-radius: 20px;
      border: 1px solid #E5E7EB;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
      animation: fadeInUp 0.6s ease-out forwards;
      opacity: 0;
      transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      overflow: hidden;
      position: relative;
    }
    .stat-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 5px;
      transform: scaleX(0);
      transition: transform 0.4s ease;
    }
    .stat-card:hover::before {
      transform: scaleX(1);
    }
    .stat-card:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
    }
    .stat-card:nth-child(1) { animation-delay: 0.1s; }
    .stat-card:nth-child(2) { animation-delay: 0.2s; }
    .stat-card:nth-child(3) { animation-delay: 0.3s; }
    .stat-card:nth-child(4) { animation-delay: 0.4s; }
    
    .stat-card-inner {
      padding: 32px;
      display: flex;
      align-items: flex-start;
      gap: 20px;
    }
    .stat-icon-wrapper {
      flex-shrink: 0;
      width: 70px;
      height: 70px;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: pulse 2s infinite;
    }
    .stat-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
    }
    .stat-title {
      font-size: 14px;
      font-weight: 600;
      color: #6B7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .stat-value {
      font-size: 2.75rem;
      font-weight: 900;
      color: #111827;
      line-height: 1;
    }
    .stat-subtext {
      font-size: 13px;
      color: #9CA3AF;
      font-weight: 500;
    }
    .stat-trend {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 700;
      margin-top: 4px;
    }
    .stat-value .spinner { width: 2rem; height: 2rem; }

    .stat-card.theme-indigo::before { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
    .stat-card.theme-indigo .stat-icon-wrapper { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; }
    .stat-card.theme-amber::before { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
    .stat-card.theme-amber .stat-icon-wrapper { background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); color: #92400e; }
    .stat-card.theme-emerald::before { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
    .stat-card.theme-emerald .stat-icon-wrapper { background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); color: #065f46; }
    .stat-card.theme-violet::before { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
    .stat-card.theme-violet .stat-icon-wrapper { background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%); color: #5b21b6; }

    /* --- Main Grid & Chart Cards --- */
    .dashboard-main-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 24px;
      margin-bottom: 32px;
    }
    .chart-card {
      background: #FFFFFF;
      border-radius: 20px;
      border: 1px solid #E5E7EB;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
      display: flex;
      flex-direction: column;
      transition: all 0.3s ease;
      overflow: hidden;
    }
    .chart-card:hover {
      box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
      transform: translateY(-4px);
    }
    .large-chart {
      grid-column: span 2;
      animation: fadeInUp 0.6s ease-out 0.5s forwards;
      opacity: 0;
    }
    .small-chart {
      grid-column: span 1;
      animation: fadeInUp 0.6s ease-out 0.6s forwards;
      opacity: 0;
    }
    .chart-header {
      padding: 28px 32px;
      border-bottom: 1px solid #F3F4F6;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .card-title {
      font-size: 1.25rem;
      font-weight: 800;
      color: #111827;
      margin: 0;
    }
    .chart-badge {
      background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
      color: #4338ca;
      padding: 6px 14px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .chart-loader {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 380px;
    }
    .recharts-wrapper {
      padding: 20px 32px 32px 32px;
    }

    /* Recent Activity */
    .recent-activity {
      grid-column: span 3;
      animation: fadeInUp 0.6s ease-out 0.7s forwards;
      opacity: 0;
    }
    .view-all-link {
      font-size: 14px;
      font-weight: 700;
      color: #667eea;
      text-decoration: none;
      transition: all 0.3s ease;
    }
    .view-all-link:hover {
      color: #4338ca;
      transform: translateX(4px);
      display: inline-block;
    }
    .recent-body {
      padding: 0;
      max-height: 500px;
      overflow-y: auto;
    }
    .recent-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    .recent-item {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 20px 32px;
      border-bottom: 1px solid #F3F4F6;
      transition: all 0.3s ease;
    }
    .recent-item:last-child {
      border-bottom: none;
    }
    .recent-item:hover {
      background: #F9FAFB;
      transform: translateX(8px);
    }
    .avatar-circle {
      width: 52px;
      height: 52px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 1.125rem;
      flex-shrink: 0;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      border: 3px solid #ffffff;
    }
    .item-info {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
      overflow: hidden;
    }
    .item-name { 
      font-weight: 700;
      font-size: 1rem;
      color: #111827;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .item-detail { 
      font-size: 14px;
      color: #6B7280;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .item-time { 
      font-size: 13px;
      color: #9CA3AF;
      font-weight: 600;
      flex-shrink: 0;
      background: #F3F4F6;
      padding: 6px 12px;
      border-radius: 12px;
    }
    .no-data-message {
      text-align: center;
      padding: 60px 20px;
    }
    .no-data-text {
      color: #6B7280;
      font-size: 1.125rem;
      font-weight: 600;
      margin: 0 0 8px 0;
    }
    .no-data-subtext {
      color: #9CA3AF;
      font-size: 0.875rem;
      margin: 0;
    }

    /* Responsive */
    @media (max-width: 1024px) {
      .dashboard-main-grid { grid-template-columns: 1fr; }
      .large-chart, .small-chart { grid-column: span 1; }
    }
    @media (max-width: 768px) {
      .dashboard-header { padding: 32px 24px; }
      .page-title { font-size: 1.875rem; }
      .filter-buttons { width: 100%; }
      .preset-btn { flex: 1 1 calc(50% - 6px); justify-content: center; min-width: 0; }
      .date-picker-wrapper { width: 100%; }
      .date-picker-toggle { width: 100%; justify-content: space-between; }
      .stats-grid { grid-template-columns: 1fr; }
      .stat-card-inner { padding: 24px; }
      .chart-header { padding: 20px 24px; }
      .recharts-wrapper { padding: 16px 24px 24px 24px; }
      .recent-item { padding: 16px 24px; }
    }
    @media (max-width: 480px) {
      .header-badge { font-size: 10px; padding: 6px 16px; }
      .page-subtitle { font-size: 1rem; }
      .preset-btn, .date-picker-toggle { padding: 10px 16px; font-size: 13px; }
      .stat-icon-wrapper { width: 56px; height: 56px; }
      .stat-value { font-size: 2.25rem; }
    }
  `}</style>
);

export default DashboardPage;